import React, { useEffect, useMemo, useState } from 'react';
import { MIcon } from '../../../lib/micon';
import { schuljahrKurz } from '../../../lib/schuljahr';
import { apiFetch } from '../../../lib/api';
import type { DbNote } from '../../../types';
import type { FachNote, MitarbeitAntwort, Ueberblick } from './profil-api';
import { note, notenWert, tm, wt, wtTm, zahl } from './format';
import { Hj1EndnoteSheet } from '../../noten/Hj1EndnoteSheet';

const ganz = (n: number) => note(n).replace(/,0$/, '');
const ART: Record<string, string> = { KA: 'Klassenarbeit', Test: 'Test', Muendlich: 'Mündlich (Note)', Sonstiges: 'Sonstiges' };
const hatMuendlich = (n: FachNote) => n.grundlage.wochen_beobachtet + n.grundlage.einzelnoten_gewicht > 0;

/** Welle P1c/N2: Tab Noten — alle Fächer, ehrlich erklärt, was die Zahl trägt. Werte, Gewichtung und Regeltext nur vom Server. */
export function NotenTab({ schuelerId, daten, fehler, neuLaden, dbNoten, onNoteBearbeiten, onAenderungen, onMitarbeitWoche, vorjahr }: {
  schuelerId: number;
  daten: Ueberblick | null; fehler: boolean; neuLaden: () => void;
  dbNoten: DbNote[];
  onNoteBearbeiten: (n: DbNote) => void;
  onAenderungen: () => void;
  onMitarbeitWoche: (fach: string, woche?: string) => void;
  vorjahr: React.ReactNode;
}) {
  const [mitarbeit, setMitarbeit] = useState<MitarbeitAntwort | null>(null);
  const [regelOffen, setRegelOffen] = useState(false);
  const [tabelleFach, setTabelleFach] = useState('');
  const [hj1Fach, setHj1Fach] = useState<string | null>(null);
  const faecher = useMemo(() => (daten?.noten ?? []).map(n => n.fach), [daten]);

  // Mündlich-Wochen aus …/mitarbeit (Teilwerte und Gewichtung stehen schon im Überblick)
  useEffect(() => {
    if (!daten) return;
    let aktiv = true;
    apiFetch<MitarbeitAntwort>(`/api/schueler/${schuelerId}/mitarbeit`).then(d => { if (aktiv) setMitarbeit(d); }).catch(() => {});
    return () => { aktiv = false; };
  }, [daten, schuelerId]);
  useEffect(() => { if (!tabelleFach && faecher.length) setTabelleFach(faecher[0]); }, [faecher, tabelleFach]);

  if (fehler && !daten) return <div className="ld-pu-karte"><div className="ld-pu-fehler" role="alert"><span>Konnte nicht geladen werden.</span><button type="button" onClick={neuLaden}><MIcon name="refresh" size={15} />Erneut versuchen</button></div></div>;
  if (!daten) return <div className="ld-pu-karte"><div className="ld-pu-laedt"><span /><span /></div></div>;
  if (faecher.length === 0) return <div className="ld-pu-karte"><div className="ld-pu-leer">Du hast in dieser Klasse noch kein Fach eingetragen.</div></div>;

  // Der Regelsatz kommt vom Server; unterscheidet sich die Gewichtung je Fach, steht er je Fach da
  const regeln = Array.from(new Set(daten.noten.map(n => n.regeltext)));
  return (
    <div className="ld-nt">
      <div className="ld-nt__faecher">
        {daten.noten.map(n => <FachKarte key={n.fach} n={n} klasse={daten.kopf.klasse} hj={daten.halbjahr.halbjahr} zeugnisText={!!daten.zeugnis_texte?.includes(n.fach)}
          regelOffen={regelOffen} onRegel={() => setRegelOffen(o => !o)} onHj1={() => setHj1Fach(n.fach)} />)}
      </div>
      {regelOffen && (
        <section className="ld-pu-karte ld-nt__regel" aria-label="Wie wird gerechnet?">
          <header className="ld-pu-karte__kopf"><MIcon name="calculate" size={19} /><h3>Wie wird gerechnet?</h3>
            <button type="button" className="ld-pu-link" onClick={() => setRegelOffen(false)}>Schließen</button></header>
          {regeln.length === 1 ? <p>{regeln[0]}</p>
            : <ul>{daten.noten.map(n => <li key={n.fach}><b>{n.fach}:</b> {n.regeltext}</li>)}</ul>}
          <p>{daten.noten[0]?.offen_text}</p>
        </section>
      )}
      <AlleNoten fach={tabelleFach} faecher={faecher} setFach={setTabelleFach} daten={daten} dbNoten={dbNoten} mitarbeit={mitarbeit}
        onNoteBearbeiten={onNoteBearbeiten} onAenderungen={onAenderungen} onMitarbeitWoche={onMitarbeitWoche} />
      {vorjahr}
      <Hj1EndnoteSheet open={hj1Fach !== null} schueler={{ id: schuelerId, name: daten.kopf.name }} klasseId={daten.kopf.klasse_id}
        fach={hj1Fach ?? ''} schuljahr={daten.halbjahr.schuljahr} onClose={() => setHj1Fach(null)}
        onGespeichert={() => { setHj1Fach(null); neuLaden(); }} />
    </div>
  );
}

function FachKarte({ n, klasse, hj, regelOffen, onRegel, onHj1, zeugnisText = false }: { n: FachNote; klasse: string | null; hj: number; regelOffen: boolean; onRegel: () => void; onHj1: () => void; zeugnisText?: boolean }) {
  const g = n.grundlage;
  const kas = n.klassenarbeiten ?? [];
  const geplant = kas.filter(k => k.status === 'geplant');
  const kaGesamt = g.ka_geschrieben + g.ka_geplant;
  const offen = n.status === 'offen';
  const hinweis = g.ka_geschrieben === 0
    ? <><b>Noch keine Klassenarbeit.</b> Die {offen ? 'Tendenz' : 'Note'} beruht bisher nur auf Mündlich.{geplant[0]?.datum ? ` KA 1 ist für ${wt(geplant[0].datum)} ${tm(geplant[0].datum)} geplant.` : ''}{offen && g.wochen_beobachtet + g.einzelnoten_gewicht < g.wochen_gesamt / 2 ? ` Bisher ${g.wochen_beobachtet} von ${g.wochen_gesamt} Wochen beobachtet.` : ''}</>
    : n.verrechnet && n.anker != null
      ? <>Klassenarbeiten zählen {n.gewichtung.schriftlich} %, Mündlich {n.gewichtung.muendlich} %. Deine HJ1-Endnote ({ganz(n.anker)}) fließt zur Hälfte ein.</>
      : null;
  const hj1Fehlt = hj === 2 && n.hj1_endnote_fehlt && n.status !== 'final';
  return (
    <section className={`ld-pu-karte ld-nt-fach${offen ? ' ld-nt-fach--offen' : ''}`} aria-label={`Noten ${n.fach}`}>
      <div className="ld-nt-fach__kopf">
        <div><h3>{n.fach}</h3><span>{klasse ? `${klasse} · ` : ''}Halbjahr {hj}{zeugnisText ? ' · Zeugnis-Text gespeichert' : ''}</span></div>
        <div className="ld-nt-fach__note">
          {offen ? <><b className="ld-nt-fach__offen">offen</b><span className="ld-nt-badge ld-nt-badge--offen">noch offen</span>{n.tendenz != null && <small>Tendenz {note(n.tendenz)}</small>}</>
            : <><b>{notenWert(n)}</b><span className={`ld-nt-badge${n.status === 'final' ? ' ld-nt-badge--final' : ''}`}>{n.status === 'final' ? 'Zeugnisnote' : 'vorläufig'}</span></>}
        </div>
      </div>
      <div className="ld-nt-balken">
        <div className="ld-nt-balken__zeile">
          <span className="ld-nt-balken__label">Klassenarbeiten<small>{n.gewichtung.schriftlich} %</small></span>
          <span className="ld-nt-balken__spur"><span style={{ width: `${kaGesamt ? (g.ka_geschrieben / kaGesamt) * 100 : 0}%` }} /></span>
          <span className="ld-nt-balken__wert"><b>{n.schriftlich != null ? note(n.schriftlich) : '—'}</b><small>{g.ka_geschrieben === 0 ? 'noch keine' : kaGesamt ? `${g.ka_geschrieben} von ${kaGesamt}` : `${g.ka_geschrieben}`}</small></span>
        </div>
        <div className="ld-nt-balken__zeile">
          <span className="ld-nt-balken__label">Mündlich<small>{n.gewichtung.muendlich} %</small></span>
          <span className="ld-nt-balken__spur ld-nt-balken__spur--hell"><span style={{ width: `${g.wochen_gesamt ? Math.min(1, (g.wochen_beobachtet + g.einzelnoten_gewicht) / g.wochen_gesamt) * 100 : 0}%` }} /></span>
          <span className="ld-nt-balken__wert"><b>{n.muendlich != null && hatMuendlich(n) ? (n.muendlich_offen ? 'offen' : note(n.muendlich)) : '—'}</b>
            <small>{g.wochen_beobachtet} von {g.wochen_gesamt} Wochen{g.einzelnoten ? ` + ${g.einzelnoten} ${g.einzelnoten === 1 ? 'Einzelnote' : 'Einzelnoten'}` : ''}</small></span>
        </div>
      </div>
      {kas.length > 0 && (
        <div className="ld-nt-chips">
          {kas.map((k, i) => k.status === 'geschrieben'
            ? <span key={`g${i}`} className="ld-nt-chip">{k.art === 'Test' ? 'Test' : 'KA'} · {tm(k.datum!)} · <b>{ganz(k.note!)}</b></span>
            : k.status === 'geplant'
              ? <span key={`p${i}`} className="ld-nt-chip ld-nt-chip--geplant">KA · {tm(k.datum!)} geplant</span>
              : <span key={`o${i}`} className="ld-nt-chip ld-nt-chip--geplant">KA {k.nummer} · noch ohne Termin</span>)}
        </div>
      )}
      {hinweis && <div className={`ld-nt-hinweis${g.ka_geschrieben === 0 ? ' ld-nt-hinweis--warn' : ''}`}><MIcon name={g.ka_geschrieben === 0 ? 'error' : 'info'} size={16} /><span>{hinweis}</span></div>}
      {hj1Fehlt && (
        <div className="ld-nt-hinweis ld-nt-hinweis--warn" role="status">
          <MIcon name="error" size={16} />
          <span><b>HJ1-Endnote fehlt.</b> Halbjahr 2 zeigt den Leistungsstand allein.{' '}
            <button type="button" className="ld-pu-link" onClick={onHj1}>Jetzt eintragen</button></span>
        </div>
      )}
      {n.vorjahr && <div className="ld-nt-hinweis"><MIcon name="history_edu" size={16} /><span>Vorjahr: {ganz(n.vorjahr.zeugnisnote)} (Halbjahr {n.vorjahr.halbjahr} {schuljahrKurz(n.vorjahr.schuljahr)}) — nur zur Information, zählt nicht mit.</span></div>}
      <button type="button" className="ld-pu-link ld-nt-fach__regel" aria-expanded={regelOffen} onClick={onRegel}><MIcon name="help" size={15} />Wie wird gerechnet?</button>
    </section>
  );
}

type Zeile = { key: string; datum: string; datumText: string; art: string; icon: string; note: string; zaehlt: string; bemerkung: string | null; onEdit: () => void };
const wochen = (g: number) => `${zahl(g, 1).replace(/,0$/, '')} ${g === 1 ? 'Woche' : 'Wochen'}`;
function AlleNoten({ fach, faecher, setFach, daten, dbNoten, mitarbeit, onNoteBearbeiten, onAenderungen, onMitarbeitWoche }: {
  fach: string; faecher: string[]; setFach: (f: string) => void; daten: Ueberblick; dbNoten: DbNote[]; mitarbeit: MitarbeitAntwort | null;
  onNoteBearbeiten: (n: DbNote) => void; onAenderungen: () => void; onMitarbeitWoche: (fach: string, woche?: string) => void;
}) {
  const { von, bis } = daten.halbjahr;
  const gew = daten.noten.find(n => n.fach === fach)?.gewichtung;
  const zeilen: Zeile[] = [
    ...dbNoten.filter(n => n.fach.trim() === fach && n.datum.slice(0, 10) >= von && n.datum.slice(0, 10) <= bis).map(n => ({
      key: `n${n.id}`, datum: n.datum.slice(0, 10), datumText: wtTm(n.datum), art: `${ART[n.art] ?? n.art}${n.gewichtung !== 1 ? ` ×${zahl(n.gewichtung, 1).replace(/,0$/, '')}` : ''}`,
      icon: n.art === 'KA' ? 'description' : n.art === 'Test' ? 'task_alt' : 'edit_note', note: ganz(n.note),
      zaehlt: n.art === 'KA' || n.art === 'Test' ? `schriftlich${gew ? ` (${gew.schriftlich} %)` : ''}` : `mündlich, wie ${wochen(n.gewichtung ?? 1)}`,
      bemerkung: n.kommentar, onEdit: () => onNoteBearbeiten(n),
    })),
    ...((mitarbeit?.faecher.find(f => f.fach === fach)?.wochen ?? []).filter(w => w.status === 'beobachtet').map(w => ({
      key: `w${w.von}`, datum: w.von, datumText: `KW ${w.kw}`, art: 'Mündlich', icon: 'back_hand', note: w.wert == null ? '—' : ganz(w.wert) + (w.tendenz ?? ''),
      zaehlt: `mündlich${gew ? ` (${gew.muendlich} %)` : ''}`, bemerkung: w.bemerkung ?? null, onEdit: () => onMitarbeitWoche(fach, w.von),
    }))),
  ].sort((a, b) => b.datum.localeCompare(a.datum));
  return (
    <section className="ld-pu-karte ld-nt-tabelle">
      <header className="ld-nt-tabelle__kopf">
        <h3><MIcon name="format_list_bulleted" size={19} />Alle Noten · {fach}<small>Halbjahr {daten.halbjahr.halbjahr}</small></h3>
        <div className="ld-nt-tabelle__aktionen">
          {faecher.length > 1 && (
            <div className="ld-nt-umschalter" role="group" aria-label="Fach">
              {faecher.map(f => <button key={f} type="button" aria-pressed={f === fach} className={f === fach ? 'an' : ''} onClick={() => setFach(f)}>{f}</button>)}
            </div>
          )}
          <button type="button" className="ld-pk-aktion" onClick={onAenderungen}><MIcon name="history" size={17} />Änderungen</button>
        </div>
      </header>
      {zeilen.length === 0 ? <div className="ld-pu-leer">Noch keine Note und keine Beobachtung in {fach} in diesem Halbjahr.</div> : (
        <table className="ld-nt-tab">
          <thead><tr><th>Datum</th><th>Art</th><th>Note</th><th>Zählt</th><th>Bemerkung</th><th><span className="ld-sr">Bearbeiten</span></th></tr></thead>
          <tbody>
            {zeilen.map(z => (
              <tr key={z.key}>
                <td>{z.datumText}</td>
                <td><span className="ld-nt-tab__art"><MIcon name={z.icon} size={15} />{z.art}</span></td>
                <td className="ld-nt-tab__note">{z.note}</td>
                <td>{z.zaehlt}</td>
                <td className={z.bemerkung ? '' : 'ld-nt-tab__ohne'}>{z.bemerkung || 'ohne Bemerkung'}</td>
                <td><button type="button" className="ld-pk-icon ld-pk-icon--klein" aria-label={`${z.art} vom ${z.datumText} bearbeiten`} onClick={z.onEdit}><MIcon name="edit" size={16} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
